import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from "@material-ui/core";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Phone as PhoneIcon,
  Refresh as RefreshIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  configCard: {
    marginBottom: theme.spacing(3),
  },
  statusBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  statusSuccess: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  statusError: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
  phoneNumbersList: {
    marginTop: theme.spacing(2),
  },
}));

const TwilioConfig = () => {
  const classes = useStyles();

  const [accountSid, setAccountSid] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [webhookBaseUrl, setWebhookBaseUrl] = useState("");
  
  const [testing, setTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [loadingNumbers, setLoadingNumbers] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = () => {
    // Load from environment variables (displayed for reference)
    setAccountSid(process.env.REACT_APP_TWILIO_ACCOUNT_SID || "");
    setAuthToken("••••••••••••••••"); // Masked for security
    setPhoneNumber(process.env.REACT_APP_TWILIO_PHONE_NUMBER || "");
    setWebhookBaseUrl(process.env.REACT_APP_BACKEND_URL || "");
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setConnectionStatus(null);

      const { data } = await api.post("/api/twilio/config/test");

      setConnectionStatus({
        success: data.success,
        message: data.message
      });

      if (data.success) {
        toast.success("Conexión exitosa con Twilio");
        loadAvailableNumbers();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error("Error testing connection:", error);
      setConnectionStatus({
        success: false,
        message: error.response?.data?.error || "Error al probar la conexión"
      });
      toast.error("Error al probar la conexión");
    } finally {
      setTesting(false);
    }
  };

  const loadAvailableNumbers = async () => {
    try {
      setLoadingNumbers(true);
      const { data } = await api.get("/api/twilio/config/phone-numbers");
      setAvailableNumbers(data.phoneNumbers || []);
    } catch (error) {
      console.error("Error loading phone numbers:", error);
      toast.error("Error al cargar números de teléfono");
    } finally {
      setLoadingNumbers(false);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Configuración de Twilio</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleTestConnection}
            disabled={testing}
            startIcon={testing ? <CircularProgress size={20} /> : <RefreshIcon />}
          >
            Probar Conexión
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card className={classes.configCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Credenciales de Twilio
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Configura tus credenciales de Twilio en las variables de entorno del backend.
                </Typography>

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Account SID"
                  value={accountSid}
                  margin="normal"
                  disabled
                  helperText="Configurado en TWILIO_ACCOUNT_SID"
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Auth Token"
                  value={authToken}
                  type="password"
                  margin="normal"
                  disabled
                  helperText="Configurado en TWILIO_AUTH_TOKEN"
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Número de Teléfono"
                  value={phoneNumber}
                  margin="normal"
                  disabled
                  helperText="Configurado en TWILIO_PHONE_NUMBER"
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Webhook Base URL"
                  value={webhookBaseUrl}
                  margin="normal"
                  disabled
                  helperText="Configurado en TWILIO_WEBHOOK_BASE_URL"
                />

                {connectionStatus && (
                  <Box
                    className={`${classes.statusBox} ${
                      connectionStatus.success
                        ? classes.statusSuccess
                        : classes.statusError
                    }`}
                  >
                    {connectionStatus.success ? (
                      <CheckCircleIcon />
                    ) : (
                      <ErrorIcon />
                    )}
                    <Typography variant="body2">
                      {connectionStatus.message}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Instrucciones de Configuración
                </Typography>
                <Typography variant="body2" paragraph>
                  Para configurar Twilio, agrega las siguientes variables de entorno en tu archivo <code>.env</code> del backend:
                </Typography>
                <Box component="pre" style={{ backgroundColor: "#f5f5f5", padding: 16, borderRadius: 4, overflow: "auto" }}>
                  {`TWILIO_ACCOUNT_SID=tu_account_sid
TWILIO_AUTH_TOKEN=tu_auth_token
TWILIO_PHONE_NUMBER=+1234567890
TWILIO_WEBHOOK_BASE_URL=https://tu-dominio.com

# ElevenLabs (para voz)
ELEVENLABS_API_KEY=tu_api_key
ELEVENLABS_VOICE_ID=tu_voice_id

# OpenAI (para conversación)
OPENAI_API_KEY=tu_openai_key`}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Números de Teléfono Disponibles
                </Typography>
                
                {loadingNumbers ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : availableNumbers.length > 0 ? (
                  <List className={classes.phoneNumbersList}>
                    {availableNumbers.map((number, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemIcon>
                            <PhoneIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={number.phoneNumber}
                            secondary={number.friendlyName}
                          />
                          {number.capabilities && (
                            <Box>
                              {number.capabilities.voice && (
                                <Chip label="Voz" size="small" color="primary" style={{ marginRight: 4 }} />
                              )}
                              {number.capabilities.sms && (
                                <Chip label="SMS" size="small" color="secondary" />
                              )}
                            </Box>
                          )}
                        </ListItem>
                        {index < availableNumbers.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary" align="center">
                    No hay números disponibles. Prueba la conexión primero.
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card style={{ marginTop: 16 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Estado del Sistema
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Twilio SDK"
                      secondary={connectionStatus?.success ? "Conectado" : "No conectado"}
                    />
                    {connectionStatus?.success ? (
                      <CheckCircleIcon color="primary" />
                    ) : (
                      <ErrorIcon color="disabled" />
                    )}
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="WebSocket Server"
                      secondary="Activo en /api/twilio/media-stream"
                    />
                    <CheckCircleIcon color="primary" />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Cola de Llamadas"
                      secondary="Sistema de cola activo"
                    />
                    <CheckCircleIcon color="primary" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </MainContainer>
  );
};

export default TwilioConfig;
